import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Mail, Phone, Building2, Calendar, Shield, Clock, 
  GraduationCap, FileText, BookOpen, AlertCircle, 
  CheckCircle2, User, Loader2, AtSign, Eye, EyeOff, Key, 
  UserCog, Save, Power, Edit2
} from 'lucide-react';
import { format } from 'date-fns';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useSites } from '@/hooks/useSites';
import { toast } from 'sonner';
import { ROLE_LABELS, AppRole, isSuperAdmin, isAdmin } from '@/constants/roles';
import { formatNoteText } from '@/utils/formatNoteText';


interface NoteDetail {
  id: string;
  title: string;
  content: string;
  note_type: string;
  created_at: string;
  clients?: { first_name: string; last_name: string } | null;
  isMentioned?: boolean;
}

interface StaffMember {
  id: string;
  full_name: string;
  email: string;
  phone?: string;
  position?: string;
  role?: string;
  site_name?: string;
  sites?: Array<{ id: string; name: string }>;
  is_active?: boolean;
  created_at?: string;
  last_login?: string;
}

interface StaffProfileDialogProps {
  staff: StaffMember | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpdate?: () => void;
}

const NOTE_TYPE_CONFIG: Record<string, { label: string; icon: string; color: string }> = {
  general: { label: 'General', icon: '📝', color: 'bg-slate-100 text-slate-700 border-slate-200' },
  incident: { label: 'Incident', icon: '⚠️', color: 'bg-red-100 text-red-700 border-red-200' },
};

export function StaffProfileDialog({ staff, open, onOpenChange, onUpdate }: StaffProfileDialogProps) {
  const { userRole } = useAuth();
  const queryClient = useQueryClient();
  const { sites = [] } = useSites();
  const canManage = isSuperAdmin(userRole as AppRole) || isAdmin(userRole as AppRole);
  
  // State for management actions
  const [selectedNote, setSelectedNote] = useState<NoteDetail | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editRole, setEditRole] = useState('');
  const [editSites, setEditSites] = useState<string[]>([]);
  const [editFullName, setEditFullName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  
  // Password reset state
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Reset edit/display state whenever the dialog switches to a different
  // staff member or closes. Prevents the previously typed name from leaking
  // into the next staff record.
  useEffect(() => {
    setIsEditing(false);
    setNewPassword('');
    setShowPassword(false);
    if (staff) {
      setEditRole(staff.role || 'staff');
      setEditSites(staff.sites?.map(s => s.id) || []);
      setEditFullName(staff.full_name || '');
      setDisplayName(staff.full_name || '');
    } else {
      setEditRole('');
      setEditSites([]);
      setEditFullName('');
      setDisplayName('');
    }
  }, [staff?.id, open]);

  // Initialize edit state when staff changes
  const initEditState = () => {
    if (staff) {
      setEditRole(staff.role || 'staff');
      setEditSites(staff.sites?.map(s => s.id) || []);
      setEditFullName(staff.full_name || '');
    }
  };

  // Fetch training assignments for this staff member
  const { data: trainingAssignments = [], isLoading: loadingTraining } = useQuery({
    queryKey: ['staff-training-assignments', staff?.id],
    queryFn: async () => {
      if (!staff?.id) return [];
      const { data, error } = await supabase
        .from('training_assignments')
        .select(`
          id,
          due_date,
          notes,
          created_at,
          training_materials(id, title, category, material_type)
        `)
        .eq('assigned_to_user_id', staff.id)
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!staff?.id && open
  });

  // Fetch training completions for this staff member
  const { data: trainingCompletions = [] } = useQuery({
    queryKey: ['staff-training-completions', staff?.id],
    queryFn: async () => {
      if (!staff?.id) return [];
      const { data, error } = await supabase
        .from('training_completions')
        .select(`
          id,
          completed_at,
          material_id,
          training_materials(id, title)
        `)
        .eq('user_id', staff.id)
        .order('completed_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!staff?.id && open
  });

  // Fetch assigned documents for this staff member
  const { data: assignedDocuments = [], isLoading: loadingDocs } = useQuery({
    queryKey: ['staff-assigned-documents', staff?.id],
    queryFn: async () => {
      if (!staff?.id) return [];
      const { data, error } = await supabase
        .from('document_assignments')
        .select(`
          id,
          due_date,
          notes,
          assigned_at,
          documents(id, title, file_name, file_type, category)
        `)
        .eq('assigned_to_user_id', staff.id)
        .order('due_date', { ascending: true });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!staff?.id && open
  });

  // Fetch notes created by this staff member
  const { data: createdNotes = [], isLoading: loadingCreatedNotes } = useQuery({
    queryKey: ['staff-created-notes', staff?.id],
    queryFn: async () => {
      if (!staff?.id) return [];
      const { data, error } = await supabase
        .from('notes')
        .select(`
          id,
          title,
          content,
          note_type,
          created_at,
          clients(first_name, last_name)
        `)
        .eq('created_by', staff.id)
        .is('file_url', null)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) throw error;
      return (data || []).map(note => ({ ...note, isMentioned: false }));
    },
    enabled: !!staff?.id && open
  });

  // Fetch notes where this staff member is mentioned
  const { data: mentionedNotes = [], isLoading: loadingMentionedNotes } = useQuery({
    queryKey: ['staff-mentioned-notes', staff?.id],
    queryFn: async () => {
      if (!staff?.id) return [];
      
      const { data: mentions, error: mentionsError } = await supabase
        .from('mentions')
        .select('note_id')
        .eq('mentioned_user_id', staff.id)
        .not('note_id', 'is', null);
      
      if (mentionsError) throw mentionsError;
      if (!mentions || mentions.length === 0) return [];
      
      const noteIds = mentions.map(m => m.note_id).filter(Boolean);
      
      const { data: notes, error: notesError } = await supabase
        .from('notes')
        .select(`
          id,
          title,
          content,
          note_type,
          created_at,
          clients(first_name, last_name)
        `)
        .in('id', noteIds)
        .is('file_url', null)
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (notesError) throw notesError;
      return (notes || []).map(note => ({ ...note, isMentioned: true }));
    },
    enabled: !!staff?.id && open
  });

  // Combine and deduplicate notes
  const staffNotes = [...createdNotes, ...mentionedNotes]
    .filter((note, index, self) => self.findIndex(n => n.id === note.id) === index)
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 15);
  
  const loadingNotes = loadingCreatedNotes || loadingMentionedNotes;

  if (!staff) return null;

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatRole = (role?: string) => {
    if (!role) return 'Staff';
    return ROLE_LABELS[role as AppRole] || role.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  const getDueDateStatus = (dueDate: string | null) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const diffDays = Math.ceil((due.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return { label: 'Overdue', color: 'bg-red-100 text-red-700 border-red-200' };
    if (diffDays <= 7) return { label: 'Due Soon', color: 'bg-amber-100 text-amber-700 border-amber-200' };
    return { label: 'Upcoming', color: 'bg-blue-100 text-blue-700 border-blue-200' };
  };

  const completedMaterialIds = new Set(trainingCompletions.map((c: any) => c.material_id));

  // Handle password reset to default
  const handleResetPassword = async () => {
    setIsResettingPassword(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const randomArray = new Uint8Array(20);
      crypto.getRandomValues(randomArray);
      const generatedPassword = btoa(String.fromCharCode(...randomArray)).slice(0, 20) + '!A1';

      const { error } = await supabase.functions.invoke('update-user-password', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { user_id: staff.id, password: generatedPassword }
      });

      if (error) throw error;

      toast.success('Password has been reset to the default. User will be prompted to create a new password on next login.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to reset password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  // Handle save role/sites
  const handleSaveChanges = async () => {
    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Filter out any null/undefined values from editSites
      const validSiteIds = editSites.filter(id => id != null && id !== '');

      if (validSiteIds.length === 0) {
        toast.error('Please assign at least one facility');
        setIsSaving(false);
        return;
      }

      const { error } = await supabase.functions.invoke('update-user', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          user_id: staff.id,
          role: editRole,
          siteIds: validSiteIds,
          fullName: editFullName.trim() || undefined
        }
      });

      if (error) throw error;


      const newName = editFullName.trim();
      if (newName) setDisplayName(newName);
      toast.success('Staff member updated successfully');
      setIsEditing(false);
      await queryClient.invalidateQueries({ queryKey: ['staff'] });
      onUpdate?.();
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update staff member');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle archive/deactivate
  const handleDeactivate = async () => {
    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { error } = await supabase.functions.invoke('update-user', {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: {
          user_id: staff.id,
          is_active: false
        }
      });

      if (error) throw error;

      toast.success('Staff member archived successfully');
      queryClient.invalidateQueries({ queryKey: ['staff'] });
      onOpenChange(false);
      onUpdate?.();
    } catch (error: any) {
      toast.error(error.message || 'Failed to archive staff member');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSiteToggle = (siteId: string, checked: boolean) => {
    if (checked) {
      setEditSites([...editSites, siteId]);
    } else {
      setEditSites(editSites.filter(id => id !== siteId));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="text-xl">Staff Profile</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="h-[calc(90vh-80px)]">
          <div className="p-6 space-y-6">
            {/* Header with Avatar */}
            <div className="flex items-start gap-4 p-4 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg">
              <Avatar className="h-20 w-20 border-4 border-background shadow-lg">
                <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
                  {getInitials(displayName || staff.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-2xl font-bold">{displayName || staff.full_name}</h3>
                    <p className="text-muted-foreground font-medium">{staff.position || formatRole(staff.role)}</p>
                  </div>
                  <Badge 
                    variant={staff.is_active !== false ? 'default' : 'secondary'} 
                    className={staff.is_active !== false ? 'bg-emerald-500' : ''}
                  >
                    {staff.is_active !== false ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-4 w-4" />
                    <span>{staff.email}</span>
                  </div>
                  {staff.phone && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="h-4 w-4" />
                      <span>{staff.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-1.5">
                    <Shield className="h-4 w-4" />
                    <span>{formatRole(staff.role)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs for different sections */}
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="training">Training & Docs</TabsTrigger>
                {canManage && <TabsTrigger value="manage">Manage</TabsTrigger>}
              </TabsList>

              {/* Overview Tab */}
              <TabsContent value="overview" className="space-y-4 mt-4">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <Card className="bg-blue-50/50 border-blue-100">
                    <CardContent className="p-4 text-center">
                      <Building2 className="h-5 w-5 mx-auto mb-1 text-blue-600" />
                      <div className="text-2xl font-bold text-blue-700">{staff.sites?.length || 1}</div>
                      <div className="text-xs text-blue-600">Facilities</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-emerald-50/50 border-emerald-100">
                    <CardContent className="p-4 text-center">
                      <CheckCircle2 className="h-5 w-5 mx-auto mb-1 text-emerald-600" />
                      <div className="text-2xl font-bold text-emerald-700">{trainingCompletions.length}</div>
                      <div className="text-xs text-emerald-600">Trainings Done</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-amber-50/50 border-amber-100">
                    <CardContent className="p-4 text-center">
                      <GraduationCap className="h-5 w-5 mx-auto mb-1 text-amber-600" />
                      <div className="text-2xl font-bold text-amber-700">{trainingAssignments.length}</div>
                      <div className="text-xs text-amber-600">Assigned</div>
                    </CardContent>
                  </Card>
                  <Card className="bg-purple-50/50 border-purple-100">
                    <CardContent className="p-4 text-center">
                      <FileText className="h-5 w-5 mx-auto mb-1 text-purple-600" />
                      <div className="text-2xl font-bold text-purple-700">{staffNotes.length}</div>
                      <div className="text-xs text-purple-600">Notes</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Assigned Facilities */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Assigned Facilities
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {staff.sites?.map((site) => (
                        <Badge key={site.id} variant="outline" className="py-1 px-3">
                          {site.name}
                        </Badge>
                      )) || (
                        <Badge variant="outline" className="py-1 px-3">
                          {staff.site_name || 'No facility assigned'}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Recent Notes */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Recent Notes
                      {staffNotes.length > 0 && (
                        <Badge variant="secondary" className="ml-auto">
                          {staffNotes.length}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingNotes ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : staffNotes.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No notes created by or mentioning this staff member
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {staffNotes.slice(0, 5).map((note: any) => {
                          const noteConfig = NOTE_TYPE_CONFIG[note.note_type] || NOTE_TYPE_CONFIG.general;
                          return (
                            <div 
                              key={note.id} 
                              onClick={() => setSelectedNote(note)}
                              className={`p-3 rounded-lg border-l-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors ${
                                note.note_type === 'incident' ? 'border-l-red-500' :
                                note.note_type === 'progress' ? 'border-l-emerald-500' :
                                note.note_type === 'observation' ? 'border-l-blue-500' :
                                'border-l-slate-400'
                              }`}
                            >
                              <div className="flex items-start justify-between mb-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-medium text-sm">{note.title}</span>
                                  <Badge className={`text-xs border ${noteConfig.color}`}>
                                    {noteConfig.icon} {noteConfig.label}
                                  </Badge>
                                  {note.isMentioned && (
                                    <Badge className="text-xs border bg-violet-100 text-violet-700 border-violet-200">
                                      <AtSign className="h-3 w-3 mr-1" />
                                      Mentioned
                                    </Badge>
                                  )}
                                </div>
                                <span className="text-xs text-muted-foreground whitespace-nowrap">
                                  {format(new Date(note.created_at), 'MMM d')}
                                </span>
                              </div>
                              <p className="text-sm text-muted-foreground line-clamp-2">{formatNoteText(note.content)}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Activity Info */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      Activity
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {staff.created_at && (
                      <div className="flex items-center gap-3">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          Joined: {format(new Date(staff.created_at), 'PPP')}
                        </span>
                      </div>
                    )}
                    {staff.last_login && (
                      <div className="flex items-center gap-3">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          Last login: {format(new Date(staff.last_login), 'PPp')}
                        </span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Training & Docs Tab */}
              <TabsContent value="training" className="space-y-4 mt-4">
                {/* Training Assignments */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <GraduationCap className="h-4 w-4" />
                      Training Assignments
                      {trainingAssignments.length > 0 && (
                        <Badge variant="secondary" className="ml-auto">
                          {trainingAssignments.length}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingTraining ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : trainingAssignments.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No training assignments
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {trainingAssignments.map((assignment: any) => {
                          const dueDateStatus = getDueDateStatus(assignment.due_date);
                          const isCompleted = completedMaterialIds.has(assignment.training_materials?.id);
                          
                          return (
                            <div 
                              key={assignment.id} 
                              className={`flex items-center justify-between p-3 rounded-lg border ${
                                isCompleted ? 'bg-emerald-50/50 border-emerald-200' : 'bg-muted/30'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                {isCompleted ? (
                                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                                ) : (
                                  <BookOpen className="h-5 w-5 text-muted-foreground" />
                                )}
                                <div>
                                  <p className={`font-medium text-sm ${isCompleted ? 'line-through text-muted-foreground' : ''}`}>
                                    {assignment.training_materials?.title || 'Unknown Training'}
                                  </p>
                                  <p className="text-xs text-muted-foreground capitalize">
                                    {assignment.training_materials?.category || 'General'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {assignment.due_date && !isCompleted && dueDateStatus && (
                                  <Badge className={`text-xs border ${dueDateStatus.color}`}>
                                    {dueDateStatus.label}
                                  </Badge>
                                )}
                                {assignment.due_date && (
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(assignment.due_date), 'MMM d, yyyy')}
                                  </span>
                                )}
                                {isCompleted && (
                                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                                    Completed
                                  </Badge>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Assigned Documents */}
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Assigned Documents
                      {assignedDocuments.length > 0 && (
                        <Badge variant="secondary" className="ml-auto">
                          {assignedDocuments.length}
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {loadingDocs ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                      </div>
                    ) : assignedDocuments.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No documents assigned
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {assignedDocuments.map((assignment: any) => {
                          const dueDateStatus = getDueDateStatus(assignment.due_date);
                          
                          return (
                            <div 
                              key={assignment.id} 
                              className="flex items-center justify-between p-3 rounded-lg border bg-muted/30"
                            >
                              <div className="flex items-center gap-3">
                                <FileText className="h-5 w-5 text-muted-foreground" />
                                <div>
                                  <p className="font-medium text-sm">
                                    {assignment.documents?.title || assignment.documents?.file_name || 'Unknown Document'}
                                  </p>
                                  <p className="text-xs text-muted-foreground capitalize">
                                    {assignment.documents?.category || 'General'}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {assignment.due_date && dueDateStatus && (
                                  <Badge className={`text-xs border ${dueDateStatus.color}`}>
                                    {dueDateStatus.label}
                                  </Badge>
                                )}
                                {assignment.due_date && (
                                  <span className="text-xs text-muted-foreground">
                                    {format(new Date(assignment.due_date), 'MMM d, yyyy')}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Manage Tab - Only for admins */}
              {canManage && (
                <TabsContent value="manage" className="space-y-4 mt-4">
                  {/* Role & Access */}
                  <Card>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-base flex items-center gap-2">
                          <UserCog className="h-4 w-4" />
                          Role & Access
                        </CardTitle>
                        {!isEditing ? (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => { initEditState(); setIsEditing(true); }}
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        ) : (
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setIsEditing(false)}>
                              Cancel
                            </Button>
                            <Button size="sm" onClick={handleSaveChanges} disabled={isSaving}>
                              {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                              <Save className="h-4 w-4 mr-1" />
                              Save
                            </Button>
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      {!isEditing ? (
                        <div className="grid grid-cols-[auto_1fr] gap-x-8 gap-y-3 text-sm">
                          <span className="text-muted-foreground">Name</span>
                          <span className="font-medium text-right">{staff.full_name}</span>
                          <span className="text-muted-foreground">Role</span>
                          <span className="text-right"><Badge variant="outline" className="font-normal">{formatRole(staff.role)}</Badge></span>
                          <span className="text-muted-foreground">Facilities</span>
                          <div className="flex flex-wrap gap-1.5 justify-end">
                            {staff.sites?.map((site) => (
                              <Badge key={site.id} variant="secondary" className="font-normal text-xs">{site.name}</Badge>
                            )) || <span className="text-muted-foreground">None assigned</span>}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2">
                            <Label>Full Name</Label>
                            <Input
                              value={editFullName}
                              onChange={(e) => setEditFullName(e.target.value)}
                              placeholder="Enter full name"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label>Role</Label>
                            <Select value={editRole} onValueChange={setEditRole}>
                              <SelectTrigger>
                                <SelectValue placeholder="Select role" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="super_admin">Super Admin</SelectItem>
                                <SelectItem value="program_director">Program Director</SelectItem>
                                <SelectItem value="manager">Manager</SelectItem>
                                <SelectItem value="supervisor">Supervisor</SelectItem>
                                <SelectItem value="case_manager">Case Manager</SelectItem>
                                <SelectItem value="residential_aide">Residential Aide</SelectItem>
                                <SelectItem value="staff">​</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Facility Access</Label>
                            <div className="border rounded-md p-3 space-y-2 max-h-40 overflow-y-auto">
                              {sites.map(site => (
                                <div key={site.id} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`site-${site.id}`}
                                    checked={editSites.includes(site.id)}
                                    onCheckedChange={(checked) => handleSiteToggle(site.id, !!checked)}
                                  />
                                  <Label htmlFor={`site-${site.id}`} className="text-sm font-normal cursor-pointer">
                                    {site.name}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </CardContent>
                  </Card>

                  {/* Password Reset */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        Reset Password
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <p className="text-sm text-muted-foreground">
                        This will reset the user's password to the default. They will be prompted to create a new password on their next login.
                      </p>
                      <Button 
                        onClick={handleResetPassword} 
                        disabled={isResettingPassword}
                        className="w-full"
                      >
                        {isResettingPassword && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Reset Password to Default
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Archive / Deactivate */}
                  {staff.is_active !== false && (
                    <Card className="border-destructive/50">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2 text-destructive">
                          <Power className="h-4 w-4" />
                          Archive Staff Member
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">
                          Archiving this staff member will deactivate their account and move their profile to the archive. This can be reversed later.
                        </p>
                        <Button 
                          variant="destructive" 
                          onClick={handleDeactivate}
                          disabled={isSaving}
                          className="w-full"
                        >
                          {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                          Archive Staff Member
                        </Button>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>
              )}
            </Tabs>
          </div>
        </ScrollArea>
      </DialogContent>

      {/* Note Detail Modal */}
      <Dialog open={!!selectedNote} onOpenChange={(open) => !open && setSelectedNote(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Note Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedNote && (
            <div className="space-y-4">
              <div className={`p-4 rounded-lg border-l-4 ${
                selectedNote.note_type === 'incident' ? 'border-l-red-500 bg-red-50/50' :
                selectedNote.note_type === 'progress' ? 'border-l-emerald-500 bg-emerald-50/50' :
                selectedNote.note_type === 'observation' ? 'border-l-blue-500 bg-blue-50/50' :
                'border-l-slate-400 bg-slate-50/50'
              }`}>
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-lg">{selectedNote.title}</h3>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-xs border ${(NOTE_TYPE_CONFIG[selectedNote.note_type] || NOTE_TYPE_CONFIG.general).color}`}>
                      {(NOTE_TYPE_CONFIG[selectedNote.note_type] || NOTE_TYPE_CONFIG.general).icon}{' '}
                      {(NOTE_TYPE_CONFIG[selectedNote.note_type] || NOTE_TYPE_CONFIG.general).label}
                    </Badge>
                    {selectedNote.isMentioned && (
                      <Badge className="text-xs border bg-violet-100 text-violet-700 border-violet-200">
                        <AtSign className="h-3 w-3 mr-1" />
                        Mentioned
                      </Badge>
                    )}
                  </div>
                </div>
                
                {selectedNote.clients && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                    <User className="h-4 w-4" />
                    <span>Client: {selectedNote.clients.first_name} {selectedNote.clients.last_name}</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>Created: {format(new Date(selectedNote.created_at), 'PPP \'at\' p')}</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Content</h4>
                <div className="p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm whitespace-pre-wrap">{formatNoteText(selectedNote.content)}</p>
                </div>
              </div>
              
              <div className="flex justify-end pt-2">
                <Button variant="outline" onClick={() => setSelectedNote(null)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
